import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { SortableOutline, WarningTriangleOutline } from '../../../../../icons/icons';
import { FleetMonitorSortOptions, MonitorStatusOptions, SortDirectionOptions } from '../../../../../types/dropdown-options';
import { FleetMonitorSortType } from '../../../../../types/settings';
import { MyFleetAirportModeEnum, MyFleetMode } from '../../../../../udView/udview';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import CustomPopover from '../../../../components/layout/CustomPopover';
import { OpenInMultiWindowButton } from '../../../../components/multi-window/OpenMultiWindowButton';
import { WorkspaceWindowPanelType } from '../../../../components/multi-window/types';
import useMyFleetIsDefined from '../../../../hooks/my-fleet-is-defined';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { setSetting } from '../../../../state/slices/settings-slice';
import { PanelTitleTrailingContent } from '../HomePanel';
import { FleetMonitorList, useFleetMonitorSort } from './FleetMonitorList';

export default function FleetMonitor() {
    const sortBy = useFleetMonitorSort();
    const sortDir = useAppSelector((x) => x.settings.fleetMonitorSortOrder);
    const minimumMonitorLevel = useAppSelector((x) => x.settings.fleetMonitorMinimumLevel);
    const splitViewEligible = useAppSelector(
        (x) => x.settings.myFleetMode === MyFleetMode.airport && x.settings.myFleetAirportMode === MyFleetAirportModeEnum.departureArrivals
    );
    const collapsedView = useAppSelector((x) => x.settings.fleetMonitorCollapsedView);
    const useSplitView = useAppSelector((x) => x.settings.fleetMonitorAirportSplitView);
    const myFleetMode = useAppSelector((x) => x.settings.myFleetMode);
    const myFleetIsDefined = useMyFleetIsDefined();
    const sortOptions = useMemo(
        () =>
            myFleetMode === MyFleetMode.airport
                ? FleetMonitorSortOptions
                : FleetMonitorSortOptions.filter((x) => x.value !== FleetMonitorSortType.departureTimeEta),
        [myFleetMode]
    );

    const dispatch = useAppDispatch();

    return (
        <div className="h-100 d-flex flex-column">
            <PanelTitleTrailingContent>
                <div className="d-flex">
                    <OpenInMultiWindowButton type={WorkspaceWindowPanelType.fleetMonitor} />
                    <CustomPopover
                        trigger={
                            <Button
                                className="button-small"
                                svgIcon={SortableOutline}
                                title={'Sort and filter options'}
                                fillMode={'flat'}
                            />
                        }
                        closeOnClick={false}
                        offsetOptions={{
                            crossAxis: -5
                        }}
                        placement="bottom">
                        <div className="p-2 position-relative d-flex flex-column gap-2" style={{ width: 300 }}>
                            <div>
                                <div>Sort by:</div>
                                <DropdownListWithValueField
                                    data={sortOptions}
                                    value={sortBy}
                                    onChange={(e) => dispatch(setSetting('defaultFlightMonitorSortType', e.value))}
                                />
                            </div>
                            <div>
                                <div>Direction:</div>
                                <DropdownListWithValueField
                                    data={SortDirectionOptions}
                                    value={sortDir}
                                    onChange={(e) => dispatch(setSetting('fleetMonitorSortOrder', e.value))}
                                />
                            </div>
                            <div>
                                <div>Minimum monitor level:</div>
                                <DropdownListWithValueField
                                    data={MonitorStatusOptions}
                                    value={minimumMonitorLevel}
                                    onChange={(e) => dispatch(setSetting('fleetMonitorMinimumLevel', e.value))}
                                />
                            </div>
                            <Checkbox
                                label="Collapsed view"
                                checked={collapsedView}
                                onChange={(e) => dispatch(setSetting('fleetMonitorCollapsedView', e.value))}
                            />
                            {splitViewEligible && (
                                <div>
                                    <Checkbox
                                        label="Split list (arrivals & departures)"
                                        checked={useSplitView}
                                        onChange={(e) => dispatch(setSetting('fleetMonitorAirportSplitView', e.value))}
                                    />
                                </div>
                            )}
                        </div>
                    </CustomPopover>
                </div>
            </PanelTitleTrailingContent>
            {!myFleetIsDefined && (
                <div className="p-2 secondary-text text-center">
                    <SvgIcon themeColor="warning" icon={WarningTriangleOutline} />
                    <span>You have not selected any aircraft for My Fleet. You can do it </span>
                    <Link to="/settings/aircraft">settings</Link>
                </div>
            )}
            {myFleetIsDefined && (
                <div className="flex-grow-1 min-h-0">
                    <FleetMonitorList />
                </div>
            )}
        </div>
    );
}
